/**
 * Tipi del modulo Allenamenti · frontend.
 * Speculari a apps/backend/src/models/trainingBlock.ts e trainingCategory.ts.
 *
 * Le CATEGORIE non stanno qui: arrivano sempre dall'API. Una copia locale
 * potrebbe divergere da quella del server, ed è il modo in cui i grafici
 * iniziano a raccontare numeri diversi dal database.
 */

export const TB_RESPONSIBLE = {
  PROF: 'prof',
  MISTER: 'mister',
  ENTRAMBI: 'entrambi',
} as const;
export type TbResponsible = (typeof TB_RESPONSIBLE)[keyof typeof TB_RESPONSIBLE];

/** Etichette dei responsabili, per select e badge. */
export const RESPONSIBLE_OPTIONS: { label: string; value: TbResponsible }[] = [
  { label: 'Prof', value: TB_RESPONSIBLE.PROF },
  { label: 'Mister', value: TB_RESPONSIBLE.MISTER },
  { label: 'Prof + Mister', value: TB_RESPONSIBLE.ENTRAMBI },
];

export interface ITrainingBlock {
  _id: string;
  entityId: string;
  teamId: string;
  localDate: string;
  timezone: string;
  slot: string | null;
  /**
   * Le categorie del blocco: almeno una, possono essere più d'una.
   *
   * I minuti NON si dividono: un blocco da 30' conta 30' su ogni categoria
   * (decisione 2026-08-18). Ne segue che nella vista Analisi la somma per
   * categoria supera il totale del periodo e le percentuali non fanno 100 —
   * per questo la composizione si legge a barre e non a torta.
   */
  categoryIds: string[];
  subcategoryIds: string[];
  title: string;
  durationMinutes: number;
  responsible: TbResponsible;
  order: number;
  /**
   * Contesto di gioco e scala Mallo (ADR 029). Tutti facoltativi (`null`):
   * la visibilità nel form dipende da `fieldsEnabled` delle categorie del
   * blocco; la densità m²/giocatore non arriva mai dal server, si deriva
   * sempre con `densitaDi` di @leopart/calc.
   */
  fieldWidthM: number | null;
  fieldLengthM: number | null;
  playersOnField: number | null;
  effectiveMinutes: number | null;
  malloWeight: number | null;
  flowType: 'continuo' | 'intermittente' | 'analitico' | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ITrainingSubcategory {
  slug: string;
  labelIt: string;
  order: number;
  isPhysical: boolean;
  color: string;
  /**
   * `true` se è di sistema. NON si eredita dalla macro: le proprie
   * sottocategorie stanno normalmente dentro una macro di sistema, perché di
   * sistema sono tutte e dodici.
   */
  isSystem: boolean;
}

export interface ITrainingCategory {
  slug: string;
  labelIt: string;
  color: string;
  order: number;
  isPhysical: boolean;
  /**
   * Gruppi di campi extra che la categoria abilita sul form del blocco:
   * `'contesto-gioco'` e/o `'mallo'` (ADR 029). Mai assente: `[]`.
   */
  fieldsEnabled: string[];
  isSystem: boolean;
  subcategories: ITrainingSubcategory[];
}

/**
 * Categoria o sottocategoria creata dalla società.
 *
 * Lo `slug` non si manda: lo deriva il server dall'etichetta e lo numera se è
 * già preso. È la chiave con cui i blocchi puntano alla categoria, e sceglierlo
 * dal client permetterebbe di rivendicare per sbaglio lo slug di una categoria
 * di sistema — che il server tratta come una sostituzione.
 */
export interface ICreateTrainingCategoryBody {
  labelIt: string;
  /** Esadecimale, `#RGB` o `#RRGGBB`. */
  color: string;
  /** Presente solo per una sottocategoria: lo slug della macro che la contiene. */
  parentSlug?: string | null;
  isPhysical?: boolean;
  /** Gruppi di campi extra abilitati sul form del blocco (ADR 029). */
  fieldsEnabled?: string[];
  order?: number;
}

/** Lo `slug` non è modificabile: i blocchi lo conservano come stringa. */
export interface IUpdateTrainingCategoryBody {
  labelIt?: string;
  color?: string;
  isPhysical?: boolean;
  /** Gruppi di campi extra abilitati sul form del blocco (ADR 029). */
  fieldsEnabled?: string[];
  order?: number;
  /** `false` la toglie dalle tendine senza toccare i blocchi che la usano. */
  isActive?: boolean;
  /**
   * Sposta la categoria: lo slug della nuova macro, oppure `null` per
   * promuoverla a primo livello. Ometterlo significa "non spostare" — `null`
   * e assente NON sono la stessa cosa.
   */
  parentSlug?: string | null;
}

export interface ICreateTrainingBlockBody {
  teamId: string;
  localDate: string;
  categoryIds: string[];
  subcategoryIds?: string[];
  title: string;
  durationMinutes: number;
  responsible: TbResponsible;
  order?: number;
  /** Contesto di gioco e Mallo (ADR 029): facoltativi, `null` = vuoto. */
  fieldWidthM?: number | null;
  fieldLengthM?: number | null;
  playersOnField?: number | null;
  effectiveMinutes?: number | null;
  malloWeight?: number | null;
  flowType?: 'continuo' | 'intermittente' | 'analitico' | null;
}

export type IUpdateTrainingBlockBody = Partial<
  Omit<ICreateTrainingBlockBody, 'teamId'>
>;

/**
 * Tipi di risposta di GET /training-blocks/analysis (vista Analisi, M3).
 * Speculari a apps/backend/src/training-blocks/training-blocks.types.ts.
 *
 * Niente etichette né colori qui dentro: il server manda solo slug (`key`/
 * `parentKey`), il client li risolve con useAllenamentiTaxonomy().bySlug —
 * la stessa fonte già usata da WeekBoard, per non avere due colori diversi
 * per la stessa categoria in due punti della UI.
 */
export type TrainingMixPeriodType = 'week' | 'month' | 'season';

/** Bucket della serie temporale: week→day, month→week, season→month. */
export type TrainingMixGranularity = 'day' | 'week' | 'month';

export interface ITrainingMixPeriod {
  type: TrainingMixPeriodType;
  /** YYYY-MM-DD, incluso. */
  from: string;
  /** YYYY-MM-DD, incluso. */
  to: string;
  label: string;
  granularity: TrainingMixGranularity;
}

/** `value: null` con `base: 0` se il periodo non ha nessuna seduta — mai `0`. */
export interface ITrainingMixAvgMinutesPerSession {
  value: number | null;
  base: number;
}

/** `null` se non ci sono blocchi nel periodo. */
export interface ITrainingMixDominant {
  key: string;
  minutes: number;
  percentage: number | null;
}

export interface ITrainingMixLongestBlock {
  id: string;
  localDate: string;
  title: string;
  minutes: number;
}

export interface ITrainingMixKpi {
  totalMinutes: number;
  totalBlocks: number;
  sessions: number;
  avgMinutesPerSession: ITrainingMixAvgMinutesPerSession;
  dominant: ITrainingMixDominant | null;
  longestBlock: ITrainingMixLongestBlock | null;
}

export interface ITrainingMixCategorySlice {
  key: string;
  minutes: number;
  blocks: number;
  /** null se il totale del periodo è 0. */
  percentage: number | null;
}

/**
 * Percentuale calcolata sul totale minuti dell'INTERO periodo (tutte le
 * categorie), non sul sottoinsieme fisico: coerenza fra donut (per
 * categoria) e barre (per sottocategoria) sullo stesso 100%.
 */
export interface ITrainingMixSubcategorySlice extends ITrainingMixCategorySlice {
  parentKey: string;
}

export interface ITrainingMixSeriesPoint {
  /** YYYY-MM-DD (day) · lunedì YYYY-MM-DD (week) · YYYY-MM (month). */
  bucket: string;
  minutes: number;
  /** Minuti per macro-categoria dentro il bucket — chiavi = slug categoria. */
  byCategory: Record<string, number>;
}

/** Pannello «Densità di gioco» — speculare a TrainingMixGameDensity (backend). */
export interface ITrainingMixGameDensity {
  eligibleBlocks: number;
  computedBlocks: number;
  /** Eleggibili senza dati campo/giocatori: esclusi ma da mostrare. */
  excludedBlocks: number;
  /** Minuti pesati per modalità DENTRO la banda — sempre 3 voci (anche a 0). */
  byRange: {
    key: 'stretto' | 'medio' | 'ampio' | 'match';
    minutes: number;
    byFlow: { flow: 'continuo' | 'intermittente' | 'analitico'; minutes: number }[];
  }[];
  /** Minuti pesati per modalità sui soli blocchi computed (densità valida). */
  byFlowTotal: { flow: 'continuo' | 'intermittente' | 'analitico'; minutes: number }[];
  weightedAvgDensity: number | null;
  pctOfMatchRef: number | null;
  effectiveMinutes: number;
  plannedMinutes: number;
  effectiveRatio: number | null;
}

/** Pannello «Scala Mallo & IDC» — speculare a TrainingMixMalloIdc (backend). */
export interface ITrainingMixMalloIdc {
  eligibleBlocks: number;
  missingMallo: number;
  missingFlow: number;
  ce: number | null;
  idc: number | null;
  cognitivePct: number | null;
  muscularPct: number | null;
  /** Minuti totali con peso Mallo assegnato (base di CE-tecnico e delle %). */
  weightedMinutes: number;
  /** Minuti con peso ≥ SOGLIA_COGNITIVA (numeratore di `cognitivePct`). */
  cognitiveMinutes: number;
  /** Minuti con peso ≥ SOGLIA_MUSCOLARE (numeratore di `muscularPct`). */
  muscularMinutes: number;
  byLevel: { level: 30 | 40 | 60 | 80 | 100; minutes: number }[];
  byFlow: { flow: 'continuo' | 'intermittente' | 'analitico'; minutes: number }[];
  ciRatio: number | null;
  ciAlert: boolean;
}

/**
 * Riga per gruppo dimensionale (Individuale/Piccoli/Medi/Grandi) del pannello
 * «Dimensione dei gruppi» — speculare a TrainingMixPlayerGroupSlice (backend).
 */
export interface ITrainingMixPlayerGroupSlice {
  group: 'individuale' | 'piccoli' | 'medi' | 'grandi';
  minutes: number;
  blocks: number;
  /** % sul totale minuti computed del pannello. `null` se il totale è 0. */
  pct: number | null;
  /** Media (larghezza×lunghezza) sui blocchi del gruppo con area valida. `null` se nessuno. */
  avgAreaM2: number | null;
  /** Minuti pesati per modalità DENTRO il gruppo — sempre 3 voci (anche a 0). */
  byFlow: { flow: 'continuo' | 'intermittente' | 'analitico'; minutes: number }[];
}

/**
 * Pannello «Dimensione dei gruppi» (F2, numero di giocatori in campo) —
 * speculare a TrainingMixPlayerDensity (backend).
 */
export interface ITrainingMixPlayerDensity {
  eligibleBlocks: number;
  computedBlocks: number;
  /** Eleggibili senza numero di giocatori: esclusi dal calcolo, mostrati come nota. */
  excludedBlocks: number;
  byGroup: ITrainingMixPlayerGroupSlice[];
  /** Minuti pesati per modalità sull'intero set eleggibile (come `malloIdc.byFlow`). */
  byFlowTotal: { flow: 'continuo' | 'intermittente' | 'analitico'; minutes: number }[];
  totalMinutes: number;
}

export interface ITrainingMixComparePeriod {
  from: string;
  to: string;
  label: string;
}

/** Presente solo con `?compare=1`: stesso periodo, finestra immediatamente precedente. */
export interface ITrainingMixCompare {
  period: ITrainingMixComparePeriod;
  totalMinutes: number;
  sessions: number;
  /** `null` se il periodo precedente ha 0 minuti (nascosto per spec). */
  deltaPct: number | null;
}

export interface ITrainingMixResponse {
  period: ITrainingMixPeriod;
  kpi: ITrainingMixKpi;
  byCategory: ITrainingMixCategorySlice[];
  bySubcategory: ITrainingMixSubcategorySlice[];
  series: ITrainingMixSeriesPoint[];
  /** `null` = nessun blocco di gioco nel periodo (pannello senza materia). */
  gameDensity: ITrainingMixGameDensity | null;
  /** `null` = nessun blocco di gioco nel periodo (pannello senza materia). */
  playerDensity: ITrainingMixPlayerDensity | null;
  /** `null` = nessun blocco con palla nel periodo. */
  malloIdc: ITrainingMixMalloIdc | null;
  /** Presente solo con `?compare=1`: confronto col periodo precedente. */
  compare?: ITrainingMixCompare;
}

/**
 * Ordine di visualizzazione scelto dalla società.
 *
 * Gli slug non elencati non spariscono: il server li accoda nell'ordine
 * naturale, così una categoria creata dopo l'ultimo riordino resta visibile.
 */
export interface IReorderTrainingCategoriesBody {
  /** Slug delle macro, nell'ordine voluto. */
  macroOrder?: string[];
  /** Per ogni macro, gli slug delle sue sottocategorie. */
  subOrder?: Record<string, string[]>;
}

/**
 * Riordino dei blocchi. Si manda la sequenza COMPLETA dei giorni toccati, non
 * "sposta il blocco X di una posizione": la scrittura è idempotente e due
 * trascinamenti ravvicinati non si sommano in modo imprevedibile.
 *
 * Un solo giorno = riordino verticale. Due giorni = trascinamento in
 * diagonale, cioè cambio di giorno E di posizione nella stessa scrittura.
 */
export interface IReorderTrainingBlocksBody {
  teamId: string;
  days: { localDate: string; blockIds: string[] }[];
}
